import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white text-gray-800 px-6 py-10 max-w-4xl mx-auto">
      <button
        onClick={() => navigate(-1)}
        className="mb-6 inline-flex items-center gap-2 text-sm hover:opacity-80"
      >
        <ArrowLeft size={18} />
        <span>Back</span>
      </button>

      <header className="mb-8">
        <h1 className="text-3xl font-bold">Privacy Policy</h1>
        <p className="text-sm text-gray-500 mt-2">Last updated: September 30, 2025</p>
      </header>

      <section className="space-y-6">
        <p>
          This Privacy Policy explains how Annotify collects, uses, and shares information when you
          use our websites, browser extensions, and related services (the "Service").
        </p>

        <h2 className="text-xl font-semibold">1. Information We Collect</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>Account and Contact Information:</strong> Such as your email address when you
            subscribe or contact support.
          </li>
          <li>
            <strong>Usage Data:</strong> Anonymous analytics about how you interact with the Service
            to improve performance and features.
          </li>
          <li>
            <strong>Payment Information:</strong> Payments are processed by Paddle. We do not store
            full credit card details. Please review Paddle's privacy policy for details.
          </li>
          <li>
            <strong>Content:</strong> Notes you create are stored to provide the Service. You control
            the content you add or delete.
          </li>
        </ul>

        <h2 className="text-xl font-semibold">2. How We Use Information</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li>Provide, maintain, and improve the Service</li>
          <li>Process subscriptions and send transactional communications</li>
          <li>Respond to customer support requests</li>
          <li>Detect, prevent, and address security or technical issues</li>
        </ul>

        <h2 className="text-xl font-semibold">3. Sharing</h2>
        <p>
          We share information with service providers who assist us in operating the Service (e.g.,
          hosting, analytics, payments). We may also share information to comply with legal
          obligations or protect rights and safety.
        </p>

        <h2 className="text-xl font-semibold">4. Data Retention</h2>
        <p>
          We retain information for as long as necessary to provide the Service and comply with our
          legal obligations. You may request deletion of your account data subject to certain
          limitations.
        </p>

        <h2 className="text-xl font-semibold">5. Security</h2>
        <p>
          We implement reasonable administrative, technical, and physical safeguards designed to
          protect information. No method of transmission or storage is 100% secure.
        </p>

        <h2 className="text-xl font-semibold">6. International Transfers</h2>
        <p>
          Your information may be transferred to and processed in countries other than your own.
          We take steps to ensure appropriate protections are in place when required.
        </p>

        <h2 className="text-xl font-semibold">7. Your Rights</h2>
        <p>
          Depending on your location, you may have rights to access, correct, delete, or export your
          information, and to object to or restrict certain processing. Contact us to exercise
          these rights.
        </p>

        <h2 className="text-xl font-semibold">8. Children's Privacy</h2>
        <p>
          The Service is not directed to children under 13 (or the minimum age in your jurisdiction)
          and we do not knowingly collect personal information from children.
        </p>

        <h2 className="text-xl font-semibold">9. Changes to this Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. We will post the updated version on
          this page with a new "Last updated" date.
        </p>

        <h2 className="text-xl font-semibold">10. Contact</h2>
        <p>
          If you have questions about this Policy, contact us at <a className="text-club-accent" href="mailto:privacy@annotify.app">privacy@annotify.app</a>.
        </p>
      </section>

      <footer className="mt-10">
        <Link to="/" className="text-club-accent">Back to Home</Link>
      </footer>
    </div>
  );
}
